import { ColDef, RowClickedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { aggridHeaderClass } from 'src/components/common/common';
import { GridContextWrapper } from 'src/components/common/gridWrapper/GridContextWrapper';
import Grid_Touch from 'src/components/common/gridWrapper/Grid_Touch';
import { GridCustomColumnDefs } from 'src/utils/grid/GridCustomColumnDefs';
import { buildDefaultColumnStateFromDefs } from 'src/utils/grid/columnPinUtils';
import { useGridSettingContent } from 'src/utils/grid/GridSettingContent';
import { defaultColDef, getDefaultRowIndexColumnConfig } from 'src/utils/grid/gridStyleUtils';
import { formatDate } from 'src/utils/string/stringUtil';

import { defaultWMS1030_R01HeaderDto, WMS1030_R01HeaderDto } from './Dto_WMS1030_R01';
import { useWMS1030_R01Store } from './Store_WMS1030_R01';
import { cellDateComparator, onlyTouchGridStyle } from 'src/utils/grid/gridFunctionUtil';

const COLUMN_STATE_KEY = 'WMS1030_R01-grid-column-state';

interface Props {
	gridRef: React.RefObject<AgGridReact<WMS1030_R01HeaderDto> | null>;
	handleCreateClick: () => void;
	defaultWMS1030_R01HeaderRow: WMS1030_R01HeaderDto;
}

export const Table_WMS1030_R01 = (props: Props) => {
	const { gridRef, handleCreateClick, defaultWMS1030_R01HeaderRow } = props;

	const {
		WMS1030_R01List,
		selectedWMS1030_R01, setSelectedWMS1030_R01, initSelectedWMS1030_R01,
		columnState, setColumnState,
		scrollTop, setScrollTop,
		setDetailScrollTop, setLotScrollTop,
	} = useWMS1030_R01Store();

	const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

	const {
		getRowId,
		settingRowIndex,
		createRowClassRules,
		paddedGridData,
		ensureSelectedRow,
	} = useGridSettingContent({
		gridRef,
		key: 'wmsOrderNo',
		gridData: WMS1030_R01List,
		defaultGridData: defaultWMS1030_R01HeaderRow,
		selectedRow: selectedWMS1030_R01,
	});

	const headerNames: Record<string, string> = {
		rowIndex: '',
		compNm: '사업장',
		dueDt: '납기일자',
		customCd: '상호코드',
		customNm: '상호명',
	};

	const getCellStyle = useCallback((params: unknown, key: string) => onlyTouchGridStyle(key, 'compNm')(params), []);

	const baseColumnDefs: ColDef<WMS1030_R01HeaderDto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: '사업장', colId: 'compNm', field: 'compNm', width: 100,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'compNm'),
		},
		{
			headerName: '수주번호', colId: 'salesOrderNo', field: 'salesOrderNo', width: 130,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'salesOrderNo'),
		},
		{
			headerName: '납기일', colId: 'dueDt', field: 'dueDt', width: 107,
			valueFormatter: (p: { data?: WMS1030_R01HeaderDto }) =>
				p.data?.dueDt ? formatDate(new Date(p.data.dueDt)) : '',
			comparator: cellDateComparator,
			cellStyle: (p) => getCellStyle(p, 'dueDt'),
		},
		{
			headerName: '상호코드', colId: 'customCd', field: 'customCd', width: 80,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'customCd'),
		},
		{
			headerName: '상호명', colId: 'customNm', field: 'customNm', width: 180,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'customNm'),
		},
	], [headerNames]);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, [])
	, [baseColumnDefs]);

	const columnDefs: ColDef<WMS1030_R01HeaderDto>[] = useMemo(() => {
		if (columnState === null) return baseColumnDefs;
		return GridCustomColumnDefs<WMS1030_R01HeaderDto>({
			baseColumnDefs,
			columnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [columnState, settingRowIndex, baseColumnDefs, headerNames]);

	const handleRowClick = useCallback((e: RowClickedEvent<WMS1030_R01HeaderDto>) => {
		if (e.node.rowPinned === 'bottom') return;
		const rowData = e.data;
		if (!rowData) return;

		const isSelected = selectedWMS1030_R01?.wmsOrderNo === rowData.wmsOrderNo;
		if (isSelected) {
			setSelectedWMS1030_R01(undefined);
			handleCreateClick();
		} else {
			setSelectedWMS1030_R01(rowData);
		}
		setDetailScrollTop(null);
		setLotScrollTop(null);
	}, [selectedWMS1030_R01, setSelectedWMS1030_R01, handleCreateClick, setDetailScrollTop, setLotScrollTop]);

	const pinnedBottomRow = useMemo(() => ([{
		rowIndex: '',
		compNm: `${WMS1030_R01List.length.toLocaleString()}건`,
		orderAmt: WMS1030_R01List.reduce((sum, item) => (sum + item.orderAmt), 0).toLocaleString(),
		editable: false,
	}]), [WMS1030_R01List]);

	useEffect(() => { ensureSelectedRow(); }, [WMS1030_R01List, selectedWMS1030_R01]);

	return (
		<GridContextWrapper<WMS1030_R01HeaderDto>
			enableCellCalculation
			gridRef={gridRef}
			paddedGridData={paddedGridData}
			initSelected={initSelectedWMS1030_R01}
			defaultRowData={defaultWMS1030_R01HeaderRow}
			baseColumnDefs={columnState ? columnDefs : baseColumnDefs}
			defaultColumnState={defaultColumnState}
			setColumnState={setColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			menuItems={undefined}
		>
			<Grid_Touch<WMS1030_R01HeaderDto>
				ref={gridRef}
				rowData={paddedGridData}
				columnDefs={columnDefs}
				defaultColDef={defaultColDef}
				getRowId={getRowId}
				rowClassRules={createRowClassRules<WMS1030_R01HeaderDto>({
					idKey: 'wmsOrderNo',
					selectedList: selectedWMS1030_R01 ? [selectedWMS1030_R01.wmsOrderNo] : [],
					deletedList: [],
				})}
				onRowClicked={handleRowClick}
				onCellKeyDown={undefined}
				pinnedBottomRowData={pinnedBottomRow}
				setHoveredRowIndex={setHoveredRowIndex}
				columnStateKey={COLUMN_STATE_KEY}
				setColumnState={setColumnState}
				scrollTop={scrollTop}
				setScrollTop={setScrollTop}
			/>
		</GridContextWrapper>
	);
};
